import { useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";

export type Pillar = 'Performance' | 'Conversion' | 'Trust' | 'Usability' | 'Accessibility';

export interface ResearchCitation {
  id: string;
  inline: string;
  whyItMatters: string;
  source: string;
  url?: string;
}

export interface ObservedMetric {
  label: string;
  value: string | number;
  unit?: string;
}

export interface EnrichedFinding {
  id: string;
  title: string;
  description: string;
  detailedInsight: string;
  recommendedFix: string | string[];
  impact: "high" | "medium" | "low";
  category: string;
  pillar: Pillar;
  researchCitations: ResearchCitation[];
  observedMetric?: ObservedMetric;
  testVariation?: {
    control: string | { html: string; label: string };
    variation: string | { html: string; label: string };
  };
  screenshotArea?: {
    x: number;
    y: number;
    width: number;
    height: number;
    description: string;
  };
}

interface EnrichedFindingsAccordionProps {
  findings: EnrichedFinding[] | null;
  loading?: boolean;
}

export function EnrichedFindingsAccordion({
  findings,
  loading = false,
}: EnrichedFindingsAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number>(0);
  const [interacted, setInteracted] = useState<Set<string>>(new Set());
  const [expandedResearch, setExpandedResearch] = useState<Set<string>>(new Set());

  const handleAccordionToggle = (index: number, findingId: string) => {
    setOpenIndex(openIndex === index ? -1 : index);
    setInteracted((prev) => new Set([...prev, findingId]));
  };

  const toggleResearch = (citationId: string) => {
    setExpandedResearch((prev) => {
      const next = new Set(prev);
      if (next.has(citationId)) {
        next.delete(citationId);
      } else {
        next.add(citationId);
      }
      return next;
    });
  };

  const getImpactColor = (
    impact: "high" | "medium" | "low"
  ): string => {
    switch (impact) {
      case "high":
        return "bg-red-500/20 text-red-300";
      case "medium":
        return "bg-yellow-500/20 text-yellow-300";
      case "low":
        return "bg-blue-500/20 text-blue-300";
    }
  };

  const getPillarColor = (pillar: Pillar): string => {
    const colors: Record<Pillar, string> = {
      Performance: "bg-cyan-500/20 text-cyan-300",
      Conversion: "bg-emerald-500/20 text-emerald-300",
      Trust: "bg-violet-500/20 text-violet-300",
      Usability: "bg-orange-500/20 text-orange-300",
      Accessibility: "bg-pink-500/20 text-pink-300",
    };
    return colors[pillar];
  };

  if (loading) {
    return (
      <div className="mt-8 space-y-4">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded-xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--elevated))] p-5"
          >
            <p className="text-[13px] text-[color:hsl(var(--muted-foreground))]">
              Finding {i + 1} of 3 — analyzing…
            </p>
            <div className="mt-3 h-3 w-2/3 rounded shimmer" />
            <div className="mt-2 h-3 w-5/6 rounded shimmer" />
          </div>
        ))}
      </div>
    );
  }

  if (!findings || findings.length === 0) {
    return (
      <div className="mt-8">
        <p className="text-[14px] text-[color:hsl(var(--muted-foreground))]">
          No findings available.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-3">
      {findings.map((finding, index) => (
        <div
          key={finding.id}
          className="rounded-xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--elevated))] overflow-hidden fade-in transition-all duration-200"
        >
          {/* Accordion Header */}
          <button
            onClick={() => handleAccordionToggle(index, finding.id)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-[color:hsl(var(--border))] transition-colors"
          >
            <div className="flex items-baseline gap-3 text-left flex-1">
              <span className="tabular text-[12px] font-semibold brand-grad-text">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-[16px] font-semibold text-white">
                    {finding.title}
                  </h3>
                  <span
                    className={`whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full ${getPillarColor(
                      finding.pillar
                    )}`}
                  >
                    {finding.pillar}
                  </span>
                </div>
                <p className="text-[13px] text-[color:hsl(var(--muted-foreground))]">
                  {finding.description}
                </p>
              </div>
              <span
                className={`whitespace-nowrap text-[11px] font-medium px-2.5 py-1 rounded-full ${getImpactColor(
                  finding.impact
                )}`}
              >
                {finding.impact.charAt(0).toUpperCase() +
                  finding.impact.slice(1)}{" "}
                Impact
              </span>
            </div>
            <ChevronDown
              size={20}
              className={`ml-2 transition-transform duration-300 flex-shrink-0 ${
                openIndex === index ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Accordion Content */}
          {openIndex === index && (
            <div className="border-t border-[color:hsl(var(--border))] bg-black/30 px-5 py-4 space-y-4">
              {/* Observed Metric */}
              {finding.observedMetric && (
                <div className="rounded-lg bg-[color:hsl(var(--surface))] p-3 border border-[color:hsl(var(--border))]">
                  <p className="text-[11px] font-medium text-[color:hsl(var(--muted-foreground))] uppercase tracking-wide">
                    What We Found
                  </p>
                  <p className="mt-2 text-[14px] font-semibold text-white">
                    {finding.observedMetric.label}:{" "}
                    <span className="text-[color:hsl(var(--primary))]">
                      {finding.observedMetric.value}
                      {finding.observedMetric.unit && ` ${finding.observedMetric.unit}`}
                    </span>
                  </p>
                </div>
              )}

              {/* Why This Matters */}
              <div>
                <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:hsl(var(--muted-foreground))]">
                  Why This Matters
                </h4>
                <p className="mt-2 text-[14px] text-[color:hsl(var(--foreground))] leading-relaxed">
                  {finding.detailedInsight}
                </p>
              </div>

              {/* Research Citations */}
              {finding.researchCitations && finding.researchCitations.length > 0 && (
                <div>
                  <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:hsl(var(--muted-foreground))]">
                    Research Backing
                  </h4>
                  <div className="mt-3 space-y-2">
                    {finding.researchCitations.map((citation) => (
                      <button
                        key={citation.id}
                        onClick={() => toggleResearch(citation.id)}
                        className="w-full text-left"
                      >
                        <div className="rounded-lg bg-[color:hsl(var(--surface))] p-3 border border-[color:hsl(var(--primary))]/30 hover:border-[color:hsl(var(--primary))]/60 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-[13px] text-[color:hsl(var(--foreground))] leading-relaxed">
                                {citation.inline}
                              </p>
                              <p className="mt-1 text-[11px] text-[color:hsl(var(--muted-foreground))]">
                                — {citation.source}
                              </p>
                            </div>
                            <ChevronDown
                              size={16}
                              className={`ml-2 flex-shrink-0 mt-0.5 transition-transform ${
                                expandedResearch.has(citation.id) ? "rotate-180" : ""
                              }`}
                            />
                          </div>

                          {/* Expanded Research Details */}
                          {expandedResearch.has(citation.id) && (
                            <div className="mt-3 pt-3 border-t border-[color:hsl(var(--primary))]/20 space-y-3">
                              <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-[color:hsl(var(--primary))]">
                                  The Research
                                </p>
                                <p className="mt-2 text-[13px] text-[color:hsl(var(--foreground))] leading-relaxed">
                                  {citation.whyItMatters}
                                </p>
                              </div>
                              {citation.url && (
                                <a
                                  href={citation.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[12px] font-medium text-[color:hsl(var(--primary))] hover:underline"
                                >
                                  Read full research
                                  <ExternalLink size={12} />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* What to Fix */}
              <div>
                <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:hsl(var(--muted-foreground))]">
                  What to Fix
                </h4>
                {Array.isArray(finding.recommendedFix) ? (
                  <ul className="mt-2 space-y-2">
                    {finding.recommendedFix.map((fix, i) => (
                      <li key={i} className="flex gap-3 text-[14px] text-[color:hsl(var(--foreground))]">
                        <span className="text-[color:hsl(var(--primary))] flex-shrink-0 mt-0.5">•</span>
                        <span>{fix}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-[14px] text-[color:hsl(var(--foreground))] leading-relaxed">
                    {finding.recommendedFix}
                  </p>
                )}
              </div>

              {/* A/B Test Idea */}
              {finding.testVariation && (
                <div>
                  <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:hsl(var(--muted-foreground))]">
                    See the Difference
                  </h4>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {/* Control */}
                    <div className="rounded-lg border border-[color:hsl(var(--border))] overflow-hidden bg-[color:hsl(var(--surface))]">
                      {typeof finding.testVariation.control === 'object' && finding.testVariation.control.html ? (
                        <>
                          <div 
                            className="p-3 bg-[color:hsl(var(--background))]"
                            dangerouslySetInnerHTML={{ __html: finding.testVariation.control.html }}
                          />
                          <div className="px-3 py-2 border-t border-[color:hsl(var(--border))] text-[11px] font-medium text-[color:hsl(var(--muted-foreground))]">
                            {finding.testVariation.control.label}
                          </div>
                        </>
                      ) : (
                        <div className="p-3">
                          <p className="text-[12px] text-[color:hsl(var(--muted-foreground))] mb-2">Current</p>
                          <p className="text-[13px] text-[color:hsl(var(--foreground))]">
                            {typeof finding.testVariation.control === 'string' ? finding.testVariation.control : ''}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Variation */}
                    <div className="rounded-lg border border-[color:hsl(var(--primary))]/50 overflow-hidden bg-[color:hsl(var(--surface))]">
                      {typeof finding.testVariation.variation === 'object' && finding.testVariation.variation.html ? (
                        <>
                          <div 
                            className="p-3 bg-[color:hsl(var(--background))]"
                            dangerouslySetInnerHTML={{ __html: finding.testVariation.variation.html }}
                          />
                          <div className="px-3 py-2 border-t border-[color:hsl(var(--primary))]/50 text-[11px] font-medium text-[color:hsl(var(--primary))]">
                            {finding.testVariation.variation.label}
                          </div>
                        </>
                      ) : (
                        <div className="p-3">
                          <p className="text-[12px] text-[color:hsl(var(--primary))] mb-2">Try This</p>
                          <p className="text-[13px] text-[color:hsl(var(--foreground))]">
                            {typeof finding.testVariation.variation === 'string' ? finding.testVariation.variation : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Where to Look */}
              {finding.screenshotArea && (
                <div>
                  <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:hsl(var(--muted-foreground))]">
                    Where to Look
                  </h4>
                  <p className="mt-2 text-[13px] text-[color:hsl(var(--foreground))]">
                    {finding.screenshotArea.description}
                  </p>
                </div>
              )}

              {/* Category Badge */}
              <div className="pt-2 border-t border-[color:hsl(var(--border))]">
                <span className="inline-block text-[11px] font-medium px-2.5 py-1 rounded-full bg-[color:hsl(var(--muted))] text-[color:hsl(var(--muted-foreground))]">
                  {finding.category}
                </span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
